import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, Text, ScrollView, ActivityIndicator, StyleSheet, Pressable, KeyboardAvoidingView, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { CommentThread, Comment, CommentContextType } from '../../shared/types/comments';
import { useCommentStore } from '../../store/commentStore';
import { CommentItem } from './CommentItem';
import { CommentInput } from './CommentInput';
import { decodeHtmlEntities } from '../../shared/utils/html-entities';
import { eventEmitter, THREAD_EVENTS } from '../../shared/utils/eventEmitter';
import { Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { addColorOpacity } from '../../shared/utils/styles';
import { logger } from '../../shared/utils/logger';

interface ThreadViewProps {
  threadId: number;
  contextType: CommentContextType;
  contextId: number;
  autoCreateThread?: boolean;
  defaultThreadTitle?: string;
}

export function ThreadView({
  threadId,
  contextType,
  contextId,
  autoCreateThread = false,
  defaultThreadTitle = 'General Discussion',
}: ThreadViewProps) {
  const styles = useStyles();
  const responsive = useResponsive();
  const { isMobile } = responsive;

  const {
    threadDetails,
    comments,
    loadingComments,
    creatingComment,
    creatingThread,
    loadThread,
    createThread,
    createComment,
    markThreadRead,
    startPolling,
    stopPolling,
  } = useCommentStore();

  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyingToAuthor, setReplyingToAuthor] = useState<string | null>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const previousCommentsLengthRef = useRef<number>(0);

  const thread = threadId ? threadDetails[threadId] : null;
  const displayComments: Comment[] = threadId ? comments[threadId] || [] : [];
  const isLoading = threadId ? loadingComments[threadId] || false : false;
  const isCreating = threadId ? creatingComment[threadId] || false : false;

  // Scroll to bottom when comments are added or updated
  const scrollToBottom = useCallback((animated = true) => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated });
    }, 100);
  }, []);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    const currentLength = displayComments.length;
    if (currentLength > previousCommentsLengthRef.current && !isLoading) {
      scrollToBottom(true);
    }
    previousCommentsLengthRef.current = currentLength;
  }, [displayComments.length, isLoading, scrollToBottom]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!isLoading && displayComments.length > 0) {
      scrollToBottom(false);
    }
  }, [isLoading, displayComments.length, scrollToBottom]);

  // Load thread on mount
  useEffect(() => {
    if (threadId) {
      loadThread(threadId);
      markThreadRead(threadId);
    }
  }, [threadId]);

  // Start polling for real-time updates
  useEffect(() => {
    startPolling(contextType, contextId);
    return () => {
      stopPolling(contextType, contextId);
    };
  }, [contextType, contextId]);

  const handleCreateComment = useCallback(
    async (content: string) => {
      let threadIdToUse = threadId;

      // If no thread exists, create one first
      if (!threadIdToUse && autoCreateThread) {
        try {
          const newThread = await createThread({
            context_type: contextType,
            context_id: contextId,
            thread_title: defaultThreadTitle,
          });

          if (newThread) {
            threadIdToUse = newThread.id;
            eventEmitter.emit(THREAD_EVENTS.CREATED, newThread);
          } else {
            throw new Error('Failed to create thread');
          }
        } catch (error: any) {
          logger.error('Error creating thread', error, 'comments');
          Alert.alert('Error', error?.message || 'Failed to create discussion thread. Please try again.');
          return;
        }
      }

      if (!threadIdToUse) {
        Alert.alert('Error', 'No discussion thread available. Please start a discussion first.');
        return;
      }

      try {
        stopPolling(contextType, contextId);

        // Optimistic update: scroll to bottom immediately
        scrollToBottom(false);

        await createComment({
          thread_id: threadIdToUse,
          parent_comment_id: replyingTo,
          content,
        });

        setReplyingTo(null);
        setReplyingToAuthor(null);

        // Reload thread to get the new comment
        await loadThread(threadIdToUse);

        // Scroll to bottom after new comment is loaded
        scrollToBottom(true);

        const storeState = useCommentStore.getState();
        const updatedThread = storeState.threadDetails[threadIdToUse];
        if (updatedThread) {
          eventEmitter.emit(THREAD_EVENTS.COMMENT_ADDED, updatedThread);
        }

        setTimeout(() => {
          startPolling(contextType, contextId);
        }, 1000);
      } catch (error: any) {
        logger.error('Error creating comment', error, 'comments');
        startPolling(contextType, contextId);
        Alert.alert('Error', error?.message || 'Failed to create comment. Please try again.');
      }
    },
    [threadId, contextType, contextId, autoCreateThread, defaultThreadTitle, replyingTo]
  );

  const handleReply = useCallback((parentId: number, authorEmail: string) => {
    setReplyingTo(parentId);
    setReplyingToAuthor(authorEmail);
  }, []);

  // Build comment tree from flat list
  const buildCommentTree = useCallback((flatComments: Comment[]) => {
    type CommentWithReplies = Comment & { replies: Comment[] };
    const commentMap = new Map<number, CommentWithReplies>(
      flatComments.map((c) => [c.id, { ...c, replies: [] }])
    );
    const rootComments: CommentWithReplies[] = [];

    flatComments.forEach((comment) => {
      const commentWithReplies = commentMap.get(comment.id)!;
      if (comment.parent_comment_id) {
        const parent = commentMap.get(comment.parent_comment_id);
        if (parent) {
          parent.replies = parent.replies || [];
          parent.replies.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments;
  }, []);

  const commentTree = buildCommentTree(displayComments);

  if (!threadId && !autoCreateThread) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: styles.utils.spacing.xl,
        }}
      >
        <Text
          style={{
            fontSize: styles.utils.typography.sizes.base,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.7),
            fontFamily: styles.utils.typography.body.fontFamily,
            textAlign: 'center',
          }}
        >
          No discussion thread available
        </Text>
      </View>
    );
  }

  if (isLoading && commentTree.length === 0) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: styles.utils.spacing.xl,
        }}
      >
        <ActivityIndicator size="large" color={styles.tokens.color.primary} />
        <Text
          style={{
            marginTop: styles.utils.spacing.md,
            fontSize: styles.utils.typography.sizes.base,
            color: styles.tokens.color.onSurface,
            fontFamily: styles.utils.typography.body.fontFamily,
          }}
        >
          Loading comments...
        </Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
    >
      <View style={{ flex: 1 }}>
        {/* Thread Header */}
        {thread && (
          <View
            style={{
              padding: styles.utils.spacing.md,
              backgroundColor: styles.tokens.color.surface,
              borderBottomWidth: 1,
              borderBottomColor: styles.tokens.color.border,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text
                style={{
                  fontSize: styles.utils.typography.sizes.lg,
                  fontWeight: styles.utils.typography.weights.semibold,
                  color: styles.tokens.color.onSurface,
                  fontFamily: styles.utils.typography.body.fontFamily,
                  flex: 1,
                  marginRight: styles.utils.spacing.sm,
                }}
                numberOfLines={2}
              >
                {decodeHtmlEntities(thread.thread_title || 'Discussion')}
              </Text>
              {thread.unread_count && thread.unread_count > 0 && (
                <View
                  style={{
                    backgroundColor: styles.tokens.color.primary,
                    borderRadius: styles.utils.radius.full,
                    paddingHorizontal: styles.utils.spacing.sm,
                    paddingVertical: 4,
                    minWidth: 24,
                    alignItems: 'center',
                  }}
                >
                  <Text
                    style={{
                      color: styles.tokens.color.onPrimary,
                      fontSize: styles.utils.typography.sizes.xs,
                      fontWeight: styles.utils.typography.weights.bold,
                      fontFamily: styles.utils.typography.body.fontFamily,
                    }}
                  >
                    {thread.unread_count}
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Comments List */}
        <ScrollView
          ref={scrollViewRef}
          style={{ flex: 1, backgroundColor: styles.tokens.color.background }}
          contentContainerStyle={{
            paddingBottom: styles.utils.spacing.xl,
          }}
          onContentSizeChange={() => {
            // Auto-scroll when content size changes (new comments added)
            scrollToBottom(true);
          }}
          keyboardShouldPersistTaps="handled"
        >
        {commentTree.length === 0 ? (
          <View
            style={{
              paddingVertical: styles.utils.spacing['2xl'],
              alignItems: 'center',
            }}
          >
            <Ionicons
              name="chatbubble-outline"
              size={48}
              color={addColorOpacity(styles.tokens.color.onSurface, 0.3)}
              style={{ marginBottom: styles.utils.spacing.md }}
            />
            <Text
              style={{
                fontSize: styles.utils.typography.sizes.base,
                color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
                fontFamily: styles.utils.typography.body.fontFamily,
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              No comments yet. Be the first to comment!
            </Text>
          </View>
        ) : (
          commentTree.map((comment, index) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              index={index}
              isLastInGroup={index === commentTree.length - 1}
              onReply={(parentId) => {
                const parentComment = displayComments.find((c) => c.id === parentId);
                handleReply(parentId, parentComment?.created_by_user?.email || 'Unknown');
              }}
            />
          ))
        )}
      </ScrollView>

      {/* Comment Input */}
      {thread?.is_locked ? (
        <View
          style={{
            padding: styles.utils.spacing.md,
            backgroundColor: addColorOpacity(styles.tokens.color.error, 0.1),
            borderTopWidth: 1,
            borderTopColor: styles.tokens.color.border,
          }}
        >
          <Text
            style={{
              fontSize: styles.utils.typography.sizes.sm,
              color: styles.tokens.color.error,
              fontFamily: styles.utils.typography.body.fontFamily,
              textAlign: 'center',
            }}
          >
            This thread is locked
          </Text>
        </View>
      ) : (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: styles.tokens.color.border,
            backgroundColor: styles.tokens.color.surface,
          }}
        >
          {replyingTo && (
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingHorizontal: styles.utils.spacing.md,
                paddingTop: styles.utils.spacing.sm,
                paddingBottom: styles.utils.spacing.xs,
                backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.05),
              }}
            >
              <Text
                style={{
                  fontSize: styles.utils.typography.sizes.sm,
                  color: styles.tokens.color.primary,
                  fontFamily: styles.utils.typography.body.fontFamily,
                }}
              >
                Replying to {replyingToAuthor}
              </Text>
              <Pressable
                onPress={() => {
                  setReplyingTo(null);
                  setReplyingToAuthor(null);
                }}
              >
                <Ionicons name="close" size={18} color={styles.tokens.color.primary} />
              </Pressable>
            </View>
          )}
          <CommentInput
            onSubmit={handleCreateComment}
            placeholder={replyingTo ? 'Write a reply...' : 'Write a comment...'}
            submitLabel={isCreating ? 'Posting...' : 'Post'}
            parentAuthor={replyingToAuthor || undefined}
            onCancel={
              replyingTo
                ? () => {
                    setReplyingTo(null);
                    setReplyingToAuthor(null);
                  }
                : undefined
            }
          />
        </View>
      )}
      </View>
    </KeyboardAvoidingView>
  );
}

